import { useCallback, useMemo, useState } from "react";

type BooleanState = [
  boolean, // value
  () => void, // setTrue
  () => void, // setFalse
  () => void, // toggle
  (value: boolean) => void, // setValue
] & {
  visible: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
  setVisible: (value: boolean) => void;
};

const useBooleanState = (initial: boolean = false): BooleanState => {
  const [value, setValue] = useState<boolean>(initial);

  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);
  const toggle = useCallback(() => setValue((v) => !v), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  return useMemo(
    () =>
      Object.assign([value, setTrue, setFalse, toggle, setValue], {
        visible: value,
        show: setTrue,
        hide: setFalse,
        toggle,
        setVisible: setValue,
      }) as unknown as BooleanState,
    [value]
  );
};

export default useBooleanState;
